import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './CargoEntry.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export default function CargoEntry({ token }) {
  const { packingListId } = useParams();
  const navigate = useNavigate();

  const [packingList, setPackingList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [cargoForm, setCargoForm] = useState({
    item_number: '',
    material_description: '',
    dimensions: '',
    quantity_pieces: '',
    net_weight_kg: '',
    gross_weight_kg: '',
    pallet_count: '',
    pallet_numbers: []
  });

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showDeletePackingList, setShowDeletePackingList] = useState(false);

  useEffect(() => {
    fetchPackingList();
  }, [packingListId]);

  const fetchPackingList = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/packing-lists/${packingListId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to load packing list');
      }

      const data = await response.json();
      setPackingList(data);
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = (e) => {
    const quantity = parseInt(e.target.value) || 0;
    setCargoForm(prev => ({
      ...prev,
      quantity_pieces: e.target.value,
      pallet_numbers: Array(quantity).fill('')
    }));
  };

  const handlePalletNumberChange = (index, value) => {
    setCargoForm(prev => ({
      ...prev,
      pallet_numbers: prev.pallet_numbers.map((num, i) => i === index ? value : num)
    }));
  };

  const handleCargoFormChange = (e) => {
    const { name, value } = e.target;
    setCargoForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddCargo = async () => {
    try {
      if (!cargoForm.material_description) {
        setError('Material description is required');
        return;
      }

      const response = await fetch(`${API_URL}/packing-lists/${packingListId}/cargo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(cargoForm)
      });

      if (!response.ok) {
        throw new Error('Failed to add cargo item');
      }

      setSuccess('Cargo item added successfully');
      setError('');
      
      // Reset form and reload
      setCargoForm({
        item_number: '',
        material_description: '',
        dimensions: '',
        quantity_pieces: '',
        net_weight_kg: '',
        gross_weight_kg: '',
        pallet_count: '',
        pallet_numbers: []
      });

      setTimeout(() => {
        fetchPackingList();
        setSuccess('');
      }, 1500);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEditStart = (item) => {
    setEditingId(item.cargo_id);
    setEditForm({
      item_number: item.item_number || '',
      material_description: item.material_description || '',
      dimensions: item.dimensions || '',
      quantity_pieces: item.quantity_pieces || '',
      net_weight_kg: item.net_weight_kg || '',
      gross_weight_kg: item.gross_weight_kg || '',
      pallet_count: item.pallet_count || '',
      pallet_numbers: item.pallet_numbers || []
    });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditPalletChange = (index, value) => {
    setEditForm(prev => ({
      ...prev,
      pallet_numbers: prev.pallet_numbers.map((num, i) => i === index ? value : num)
    }));
  };

  const handleSaveEdit = async () => {
    try {
      const response = await fetch(`${API_URL}/cargo/${editingId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editForm)
      });

      if (!response.ok) {
        throw new Error('Failed to update cargo item');
      }

      setSuccess('Cargo item updated successfully');
      setEditingId(null);
      setError('');

      setTimeout(() => {
        fetchPackingList();
        setSuccess('');
      }, 1500);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteCargo = async (cargo_id) => {
    try {
      const response = await fetch(`${API_URL}/cargo/${cargo_id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to delete cargo item');
      }

      setSuccess('Cargo item deleted successfully');
      setShowDeleteConfirm(null);
      setError('');

      setTimeout(() => {
        fetchPackingList();
        setSuccess('');
      }, 1500);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeletePackingList = async () => {
    try {
      const response = await fetch(`${API_URL}/packing-lists/${packingListId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to delete packing list');
      }

      setSuccess('Packing list deleted successfully');
      setTimeout(() => {
        navigate('/packing-lists');
      }, 1500);
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) {
    return <div className="loading">Loading packing list...</div>;
  }

  if (!packingList) {
    return <div className="error">Packing list not found</div>;
  }

  return (
    <div className="cargo-entry-container">
      <div className="cargo-header">
        <div>
          <h1>Cargo Entry</h1>
          <p>Order: {packingList.order_number || 'N/A'} | Invoice: {packingList.invoice_number || 'N/A'}</p>
        </div>
        <button 
          className="btn btn-danger"
          onClick={() => setShowDeletePackingList(true)}
          title="Delete entire packing list"
        >
          🗑️ Delete Packing List
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Delete Packing List Confirmation Modal */}
      {showDeletePackingList && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Delete Packing List?</h3>
            <p>This will delete the entire packing list and all cargo items. This cannot be undone.</p>
            <div className="modal-buttons">
              <button className="btn btn-danger" onClick={handleDeletePackingList}>
                Delete
              </button>
              <button className="btn btn-secondary" onClick={() => setShowDeletePackingList(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="cargo-form-section">
        <h2>Add New Cargo Item</h2>
        <div className="form-row">
          <div className="form-group">
            <label>Item Number</label>
            <input
              type="number"
              name="item_number"
              value={cargoForm.item_number}
              onChange={handleCargoFormChange}
              placeholder="e.g., 1"
            />
          </div>
          <div className="form-group">
            <label>Material Description *</label>
            <input
              type="text"
              name="material_description"
              value={cargoForm.material_description}
              onChange={handleCargoFormChange}
              placeholder="e.g., Refractory Bricks"
              required
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Dimensions</label>
            <input
              type="text"
              name="dimensions"
              value={cargoForm.dimensions}
              onChange={handleCargoFormChange}
              placeholder="e.g., 10x20x30 cm"
            />
          </div>
          <div className="form-group">
            <label>Quantity (Pieces)</label>
            <input
              type="number"
              name="quantity_pieces"
              value={cargoForm.quantity_pieces}
              onChange={handleQuantityChange}
              placeholder="0"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Net Weight (kg)</label>
            <input
              type="number"
              name="net_weight_kg"
              value={cargoForm.net_weight_kg}
              onChange={handleCargoFormChange}
              placeholder="0"
            />
          </div>
          <div className="form-group">
            <label>Gross Weight (kg)</label>
            <input
              type="number"
              name="gross_weight_kg"
              value={cargoForm.gross_weight_kg}
              onChange={handleCargoFormChange}
              placeholder="0"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Pallet Count</label>
            <input
              type="number"
              name="pallet_count"
              value={cargoForm.pallet_count}
              onChange={handleCargoFormChange}
              placeholder="0"
            />
          </div>
        </div>

        {cargoForm.quantity_pieces && parseInt(cargoForm.quantity_pieces) > 0 && (
          <div className="pallet-numbers-section">
            <label>Pallet Serial Numbers ({cargoForm.quantity_pieces} required)</label>
            <div className="pallet-inputs">
              {cargoForm.pallet_numbers.map((num, idx) => (
                <input
                  key={idx}
                  type="text"
                  value={num}
                  onChange={(e) => handlePalletNumberChange(idx, e.target.value)}
                  placeholder={`Pallet ${idx + 1}`}
                />
              ))}
            </div>
          </div>
        )}

        <button className="btn btn-primary" onClick={handleAddCargo}>
          Add Cargo Item
        </button>
      </div>

      <div className="cargo-items-section">
        <h2>Cargo Items ({packingList.cargo_items?.length || 0})</h2>
        
        {!packingList.cargo_items || packingList.cargo_items.length === 0 ? (
          <div className="no-items">No cargo items yet</div>
        ) : (
          <div className="cargo-table-wrapper">
            <table className="cargo-table">
              <thead>
                <tr>
                  <th>Item #</th>
                  <th>Material</th>
                  <th>Qty</th>
                  <th>Remaining</th>
                  <th>Net Wt (kg)</th>
                  <th>Gross Wt (kg)</th>
                  <th>Pallets</th>
                  <th>Available</th>
                  <th>Serial #s</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {packingList.cargo_items.map(item => (
                  editingId === item.cargo_id ? (
                    <tr key={item.cargo_id} className="edit-row">
                      <td colSpan="10">
                        <div className="edit-form-inline">
                          <div className="form-row">
                            <input
                              type="number"
                              name="item_number"
                              value={editForm.item_number}
                              onChange={handleEditChange}
                              placeholder="Item #"
                            />
                            <input
                              type="text"
                              name="material_description"
                              value={editForm.material_description}
                              onChange={handleEditChange}
                              placeholder="Material"
                            />
                            <input
                              type="number"
                              name="quantity_pieces"
                              value={editForm.quantity_pieces}
                              onChange={handleEditChange}
                              placeholder="Qty"
                            />
                            <input
                              type="number"
                              name="net_weight_kg"
                              value={editForm.net_weight_kg}
                              onChange={handleEditChange}
                              placeholder="Net Wt"
                            />
                            <input
                              type="number"
                              name="gross_weight_kg"
                              value={editForm.gross_weight_kg}
                              onChange={handleEditChange}
                              placeholder="Gross Wt"
                            />
                          </div>
                          {editForm.pallet_numbers?.length > 0 && (
                            <div className="pallet-edit-section">
                              <label>Pallet Serial Numbers:</label>
                              <div className="pallet-inputs">
                                {editForm.pallet_numbers.map((num, idx) => (
                                  <input
                                    key={idx}
                                    type="text"
                                    value={num}
                                    onChange={(e) => handleEditPalletChange(idx, e.target.value)}
                                    placeholder={`Pallet ${idx + 1}`}
                                  />
                                ))}
                              </div>
                            </div>
                          )}
                          <div className="button-group">
                            <button className="btn btn-primary btn-sm" onClick={handleSaveEdit}>
                              Save
                            </button>
                            <button className="btn btn-secondary btn-sm" onClick={() => setEditingId(null)}>
                              Cancel
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    <tr key={item.cargo_id}>
                      <td>{item.item_number || '-'}</td>
                      <td>{item.material_description}</td>
                      <td>{item.quantity_pieces}</td>
                      <td className="remaining-qty">
                        {item.inventory?.quantity_remaining ?? item.quantity_pieces}
                      </td>
                      <td>{item.net_weight_kg || '-'}</td>
                      <td>{item.gross_weight_kg || '-'}</td>
                      <td>{item.pallet_count || '-'}</td>
                      <td>{item.inventory?.pallets_available ?? item.pallet_count ?? '-'}</td>
                      <td>{item.pallet_numbers?.join(', ') || 'N/A'}</td>
                      <td className="actions">
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleEditStart(item)}
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => setShowDeleteConfirm(item.cargo_id)}
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  )
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Delete Cargo Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h3>Delete Cargo Item?</h3>
              <p>This cargo item will be permanently deleted.</p>
              <div className="modal-buttons">
                <button className="btn btn-danger" onClick={() => handleDeleteCargo(showDeleteConfirm)}>
                  Delete
                </button>
                <button className="btn btn-secondary" onClick={() => setShowDeleteConfirm(null)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="nav-buttons">
        <button className="btn btn-secondary" onClick={() => navigate('/packing-lists')}>
          Back to List
        </button>
      </div>
    </div>
  );
}
